/*
  File: src/pages/LoginPage.jsx
  Purpose: Sign-in form that authenticates via `AuthContext.login` and redirects home.
  Responsibilities:
  - Collect identifier and password; show inline validation and error messages.
  - On success, navigate to root; on failure, display server error message.
*/
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifier || !password) {
      setError('Please fill in all fields');
      return;
    }
    setIsLoading(true);
    try {
      await login({ email: identifier, password });
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#0a1628] via-[#1a2448]/60 to-[#0a1628]">
      <main className="relative flex-1 flex items-stretch px-0 sm:px-6 py-0 sm:py-10">
        <div className="absolute top-4 left-4 z-10">
          <Link to="/" className="inline-flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="SceneShop"
              className="h-9 w-auto drop-shadow"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          </Link>
        </div>
        <div className="absolute inset-0">
          <img
            src={`${process.env.PUBLIC_URL}/match.png`}
            alt="hero"
            className="w-full h-full object-cover object-center opacity-60"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1628] via-[#1a2448]/10 to-[#0a1628]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628] via-transparent to-[#1a2448]/30" />
        </div>
        <div className="relative flex-1 grid grid-cols-1 lg:grid-cols-2 gap-0 lg:gap-8 w-full max-w-7xl mx-auto">
          <section className="hidden lg:flex flex-col justify-center px-10">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 text-sm mb-3">
                {['Sports', 'Series', 'Movies', 'Live TV'].map((t) => (
                  <span key={t} className="px-3 py-1 rounded-full bg-white/10 border border-white/10 text-white/80">{t}</span>
                ))}
              </div>
              <h1 className="text-4xl font-extrabold tracking-tight">Stream the game. Binge the series. Watch it live.</h1>
              <p className="mt-4 text-white/70">Sign in to continue. New to SceneShop? Create an account and start watching in minutes.</p>
              <ul className="mt-6 space-y-2 text-white/80 text-sm">
                <li>• Live football matches with instant highlights</li>
                <li>• New and classic series, all in one place</li>
                <li>• Personalized recommendations and watchlist</li>
              </ul>
            </div>
          </section>
          <div className="flex items-center justify-center p-6 sm:p-10">
            <div className="w-full max-w-md">
              <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#1a2448]/70 to-[#1a2448]/30 border border-[#4cc3e9]/50 shadow-xl">
                <div className="text-center mb-6">
                  <div className="mx-auto h-12 w-12 rounded-full bg-[#4cc3e9]/10 border border-[#4cc3e9]/30 flex items-center justify-center mb-3">
                    <span className="text-[#4cc3e9] text-xl">🔐</span>
                  </div>
                  <h2 className="text-2xl font-bold tracking-tight text-white">Welcome back</h2>
                  <p className="text-sm text-white/70 mt-1">Sign in to continue</p>
                </div>

                {error && <div className="mb-4 rounded-lg bg-red-500/10 p-3 text-sm text-red-300 border border-red-500/30">{error}</div>}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Username or Email</label>
                    <input
                      type="text"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full rounded-lg px-3 py-2 text-white placeholder-white/50 bg-gradient-to-r from-[#1a2448]/50 to-[#1a2448]/40 border border-[#4cc3e9]/20 focus:outline-none focus:border-[#4cc3e9]/50"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-white/80 mb-1">Password</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-lg px-3 py-2 text-white placeholder-white/50 bg-gradient-to-r from-[#1a2448]/50 to-[#1a2448]/40 border border-[#4cc3e9]/20 focus:outline-none focus:border-[#4cc3e9]/50"
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full rounded-lg py-2.5 bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] hover:from-[#9060ff] hover:to-[#7b3ff2] text-white font-medium border border-[#9060ff]/40 transition-all duration-200 hover:scale-[1.02]"
                  >
                    {isLoading ? 'Signing in…' : 'Sign in'}
                  </button>
                </form>

                <p className="mt-6 text-center text-sm text-white/70">
                  Not a member? <Link to="/signup" className="text-[#4cc3e9] font-medium hover:text-white">Create an account</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default LoginPage;


